/**
 * Screen Wake Lock.
 *
 * A phone that sleeps stops the camera, which ends the recording — so on the
 * phone this is not a nicety, it is what keeps the side camera alive for three
 * hours.
 *
 * Two things browsers do that this has to handle:
 *   - The lock is released automatically whenever the page is hidden, so it
 *     must be re-acquired on `visibilitychange`.
 *   - `navigator.wakeLock` is missing on older Android WebViews and on iOS
 *     before 16.4, so its absence is reported rather than thrown.
 */
import { useCallback, useEffect, useRef, useState } from "react";

interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: "release", listener: () => void) => void;
}

interface WakeLockNavigator {
  wakeLock?: {
    request: (type: "screen") => Promise<WakeLockSentinelLike>;
  };
}

export interface WakeLockHandle {
  /** True while the screen is being held awake. */
  active: boolean;
  supported: boolean;
}

export function useWakeLock(enabled: boolean): WakeLockHandle {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  const [active, setActive] = useState(false);

  const supported =
    typeof navigator !== "undefined" &&
    (navigator as WakeLockNavigator).wakeLock !== undefined;

  const acquire = useCallback(async () => {
    const api = (navigator as WakeLockNavigator).wakeLock;
    if (!api || sentinelRef.current) return;

    try {
      const sentinel = await api.request("screen");
      sentinelRef.current = sentinel;
      setActive(true);

      sentinel.addEventListener("release", () => {
        sentinelRef.current = null;
        setActive(false);
      });
    } catch {
      // Denied (low battery on some devices) — the UI tells the student to set
      // the screen timeout manually.
      setActive(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !supported) return;

    void acquire();

    // Android releases the lock every time the page is backgrounded.
    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      setActive(false);
      void sentinel?.release().catch(() => undefined);
    };
  }, [enabled, supported, acquire]);

  return { active, supported };
}
